import type { ExpenseDetailsResponse, ExpenseStatisticsResponse } from '@/domain/entities/property-expense';
import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type { PropertyExpenseDetailsRequest, PropertyExpensesRequest } from './propertyExpensesTypes';

function toPropertyKey(propertyID: string): string {
  return propertyID.trim();
}

function propertyStatisticsPath(propertyID: string): string {
  return `${getApiEndpoints().core.propertyStatistics.base}/${encodeURIComponent(propertyID)}/statistics/expenses`;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return;
    query.set(key, String(value));
  });

  return query.toString();
}

export function buildExpenseStatisticsKey({ propertyID, query = {} }: PropertyExpensesRequest): string {
  return [
    toPropertyKey(propertyID),
    query.from ?? 'default_from',
    query.to ?? 'default_to',
    query.sourceType ?? 'all_sources',
  ].join('|');
}

export function buildExpenseDetailsKey({ propertyID, query }: PropertyExpenseDetailsRequest): string {
  return [toPropertyKey(propertyID), query.periodKey, query.sourceType ?? 'all_sources'].join('|');
}

function toRejectValue(error: unknown, fallback: string): { message: string } {
  return { message: error instanceof Error && error.message ? error.message : fallback };
}

export const fetchPropertyExpenseStatistics = createAppAsyncThunk<ExpenseStatisticsResponse, PropertyExpensesRequest>(
  'propertyExpenses/fetchStatistics',
  async ({ propertyID, query = {} }, { extra: { config }, rejectWithValue }) => {
    const propertyKey = toPropertyKey(propertyID);

    try {
      const params = buildQuery({
        from: query.from,
        to: query.to,
        sourceType: query.sourceType,
      });
      const path = propertyStatisticsPath(propertyKey);
      const endpoint = params ? `${path}?${params}` : path;
      return await config.apiService.get<ExpenseStatisticsResponse>(endpoint);
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to load property expense statistics'));
    }
  },
  {
    condition: (request, { getState }) => {
      const propertyKey = toPropertyKey(request.propertyID);
      if (!propertyKey) return false;
      if (request.force) return true;

      const bucket = getState().propertyExpenses.statisticsByKey[buildExpenseStatisticsKey(request)];
      if (bucket?.loading) return false;
      if (bucket?.loaded) return false;
      return true;
    },
  },
);

export const fetchPropertyExpenseDetails = createAppAsyncThunk<ExpenseDetailsResponse, PropertyExpenseDetailsRequest>(
  'propertyExpenses/fetchDetails',
  async ({ propertyID, query }, { extra: { config }, rejectWithValue }) => {
    const propertyKey = toPropertyKey(propertyID);

    try {
      const params = buildQuery({
        periodKey: query.periodKey,
        sourceType: query.sourceType,
      });
      const endpoint = `${propertyStatisticsPath(propertyKey)}/details?${params}`;
      return await config.apiService.get<ExpenseDetailsResponse>(endpoint);
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to load property expense details'));
    }
  },
  {
    condition: (request, { getState }) => {
      const propertyKey = toPropertyKey(request.propertyID);
      if (!propertyKey || !request.query.periodKey.trim()) return false;
      if (request.force) return true;

      const bucket = getState().propertyExpenses.detailsByKey[buildExpenseDetailsKey(request)];
      if (bucket?.loading) return false;
      if (bucket?.loaded) return false;
      return true;
    },
  },
);
